import {
  cardShieldAdobeBannerMatchesLinkedAccountCount,
  filterCardShieldDashboardAccountKeyedBanners,
  isCardShieldBannerDecision,
  type AdobeBannerDecision,
} from '../containers/banner/adobeBannerDecisionUtils';

/**
 * Opt-in console logging for Member API `productFeatures.cardShield` and DSP `cardShieldVisible`.
 *
 * Enable on INT or locally:
 *   localStorage.setItem('DEBUG_CARD_SHIELD_VISIBILITY', '1'); location.reload();
 * Or add `debugCardShieldVisibility=1` to the page query.
 *
 * In `NODE_ENV === 'development'`, logging is on by default.
 */

export const CARD_SHIELD_VISIBILITY_DEBUG_LS_KEY = 'DEBUG_CARD_SHIELD_VISIBILITY';
export const CARD_SHIELD_DASHBOARD_RIBBON_LOG_TAG = '[DSP Card Shield dashboard ribbon]';

export type CardShieldDashboardRibbonDecisionCode =
  | 'auth_or_plan_not_ready'
  | 'product_feature_gate_blocked'
  | 'waiting_for_linked_accounts'
  | 'no_cardshield_rows_from_adobe'
  | 'cardshield_rows_filtered_out'
  | 'no_winning_banner_after_priority'
  | 'won_by_other_campaign'
  | 'showing_cardshield_banner';

function readDebugCardShieldVisibilityFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const fromSearch = new URLSearchParams(window.location.search).get('debugCardShieldVisibility');
    if (fromSearch === '1' || fromSearch === 'true') return true;
    const hash = window.location.hash || '';
    const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
    if (hashQuery && new URLSearchParams(hashQuery).get('debugCardShieldVisibility') === '1') return true;
  } catch {
    return false;
  }
  return false;
}

export function isCardShieldVisibilityDebugEnabled(): boolean {
  if (process.env.NODE_ENV === 'test') return false;
  if (process.env.NODE_ENV === 'development') return true;
  if (typeof window === 'undefined') return false;
  if (readDebugCardShieldVisibilityFromUrl()) return true;
  try {
    return window.localStorage.getItem(CARD_SHIELD_VISIBILITY_DEBUG_LS_KEY) === '1';
  } catch {
    return false;
  }
}

export type CardShieldMemberApiFeatureSnapshot = {
  featureStatus?: string;
  fulfillmentStatus?: string;
  serviceCode?: string;
};

export type LogCardShieldVisibilityInput = {
  /** Where DSP evaluated visibility (e.g. financial-monitoring-dashboard, adobe-dashboard-banner). */
  surface: string;
  /** Auth context only — US eligibility is Member API `productFeatures.cardShield`, not a DSP gate. */
  membershipCountryCode: string;
  memberApiCardShield: CardShieldMemberApiFeatureSnapshot | undefined;
  configEnabled: boolean;
  featureAvailable: boolean;
  visible: boolean;
};

/** Per-surface snapshot so render loops (e.g. Adobe 500ms poll) do not spam the console. */
const lastVisibilityLogSignatureBySurface = new Map<string, string>();
let lastRibbonLogSignature = '';

function buildVisibilityLogSignature(input: LogCardShieldVisibilityInput): string {
  const cardShield = input.memberApiCardShield;
  return JSON.stringify({
    visible: input.visible,
    membershipCountryCode: input.membershipCountryCode,
    configEnabled: input.configEnabled,
    featureAvailable: input.featureAvailable,
    featureStatus: cardShield?.featureStatus,
    fulfillmentStatus: cardShield?.fulfillmentStatus,
    serviceCode: cardShield?.serviceCode,
  });
}

function buildRibbonLogSignature(
  input: CardShieldDashboardRibbonDecisionInput,
  decisionReasonCode: CardShieldDashboardRibbonDecisionCode
): string {
  const rawCardShieldIds = input.adobeBannerData
    .filter((row) => isCardShieldBannerDecision(row))
    .map((row) => row.bannerId)
    .sort();
  const filteredCardShieldIds = input.adobeBannerDataForPriority
    .filter((row) => isCardShieldBannerDecision(row))
    .map((row) => row.bannerId)
    .sort();
  return JSON.stringify({
    decisionReasonCode,
    authReady: input.authReady,
    showCardShieldFeature: input.showCardShieldFeature,
    cardShieldFeatureStatus: input.memberApiCardShield?.featureStatus,
    linkedAccountsForCardShield: input.linkedAccountsForCardShield,
    linkedAccountsForCardShieldReady: input.linkedAccountsForCardShieldReady,
    rawRowCount: input.adobeBannerData.length,
    filteredRowCount: input.adobeBannerDataForPriority.length,
    rawCardShieldIds,
    filteredCardShieldIds,
    chosenBannerId: input.chosenAdobeBanner?.bannerId,
    chosenBannerPriority: input.chosenAdobeBanner?.bannerPriority,
    whiteLabelClient: input.whiteLabelClient ?? '',
    windowAdobeBannerTestCount: input.windowAdobeBannerTestCount,
    whyRibbonHidden: buildWhyRibbonHidden(input, decisionReasonCode),
  });
}

/** @internal test helper */
export function resetCardShieldVisibilityDebugLogForTests(): void {
  lastVisibilityLogSignatureBySurface.clear();
  lastRibbonLogSignature = '';
}

/**
 * Logs why `utils.cardShieldVisible` returned true/false for a surface.
 * Member API owns plan/partner/credit/LL-Std/TXM rules; DSP only reads `productFeatures.cardShield`.
 * Logs once per surface until the evaluated inputs change.
 */
export function logCardShieldVisibilityFromMemberApi(input: LogCardShieldVisibilityInput): void {
  if (!isCardShieldVisibilityDebugEnabled()) return;

  const signature = buildVisibilityLogSignature(input);
  if (lastVisibilityLogSignatureBySurface.get(input.surface) === signature) {
    return;
  }
  lastVisibilityLogSignatureBySurface.set(input.surface, signature);

  const gateReasons: string[] = [];
  if (!input.configEnabled) {
    gateReasons.push('REACT_APP_CARD_SHIELD_ENABLED is false');
  }
  if (!input.featureAvailable) {
    const status = input.memberApiCardShield?.featureStatus ?? '(missing)';
    gateReasons.push(`productFeatures.cardShield featureStatus is not AVAILABLE (${status})`);
  }

  // eslint-disable-next-line no-console -- opt-in / dev Card Shield visibility diagnostics
  console.log('[DSP Card Shield visibility]', {
    surface: input.surface,
    visible: input.visible,
    cardShieldProductGate: input.visible,
    membershipCountryCode: input.membershipCountryCode || '(not passed)',
    configEnabled: input.configEnabled,
    memberApiProductFeaturesCardShield: input.memberApiCardShield ?? null,
    featureAvailableFromMemberApi: input.featureAvailable,
    blockedBy: input.visible ? [] : gateReasons,
    hint:
      input.surface === 'adobe-dashboard-banner' && input.visible
        ? 'Member API gate only. The blue ribbon is decided separately — filter console for "[DSP Card Shield dashboard ribbon]" (whyRibbonHidden).'
        : input.visible
        ? 'Section may still hide UI for other reasons (e.g. Adobe Target, linked accounts, route guard).'
        : 'Fix Member API cardShield (or deploy flags) before Adobe ribbon / FM tile can show.',
  });
}

/** Whether {@link AdobeBanner} would render the row on this SPA path (`bannerToShowOn` / dashboard default). */
export function cardShieldAdobeBannerRendersOnPath(banner: AdobeBannerDecision | undefined, pathname: string): boolean {
  const bannerToShowOn = banner?.bannerToShowOn;
  if (Array.isArray(bannerToShowOn) && bannerToShowOn.length > 0) {
    return bannerToShowOn.includes(pathname);
  }
  return pathname === '/dashboard';
}

export type CardShieldDashboardRibbonDecisionInput = {
  authReady: boolean;
  productFeaturesKeys: string[];
  showCardShieldFeature: boolean;
  memberApiCardShield: CardShieldMemberApiFeatureSnapshot | undefined;
  isUnifiedTransactions: boolean;
  linkedAccountsForCardShield: number;
  linkedAccountsForCardShieldReady: boolean;
  adobeBannerData: AdobeBannerDecision[];
  adobeBannerDataForPriority: AdobeBannerDecision[];
  chosenAdobeBanner?: AdobeBannerDecision;
  /** Current route (AdobeBannerList `useLocation().pathname`). */
  currentPathname?: string;
  /** From auth.whiteLabelClient — when set, useAdobeConceptTesting returns no rows. */
  whiteLabelClient?: string;
  /** Rows on window.adobeBannerTest before the Adobe hook filters them. */
  windowAdobeBannerTestCount?: number;
  windowAdobeCardShieldRowCount?: number;
  windowAdobeCardShieldBannerIds?: string[];
};

export function deriveCardShieldDashboardRibbonDecisionCode(
  input: CardShieldDashboardRibbonDecisionInput
): CardShieldDashboardRibbonDecisionCode {
  if (!input.authReady) {
    return 'auth_or_plan_not_ready';
  }
  if (!input.showCardShieldFeature) {
    return 'product_feature_gate_blocked';
  }
  if (!input.linkedAccountsForCardShieldReady) {
    return 'waiting_for_linked_accounts';
  }

  const rawCardShieldRows = input.adobeBannerData.filter((row) => isCardShieldBannerDecision(row));
  if (rawCardShieldRows.length === 0) {
    return 'no_cardshield_rows_from_adobe';
  }

  const afterFilterCardShieldRows = input.adobeBannerDataForPriority.filter((row) => isCardShieldBannerDecision(row));
  if (afterFilterCardShieldRows.length === 0) {
    return 'cardshield_rows_filtered_out';
  }

  if (!input.chosenAdobeBanner) {
    return 'no_winning_banner_after_priority';
  }

  if (!isCardShieldBannerDecision(input.chosenAdobeBanner)) {
    return 'won_by_other_campaign';
  }

  return 'showing_cardshield_banner';
}

export function buildCardShieldRibbonFilterBreakdown(
  adobeBannerData: AdobeBannerDecision[],
  showCardShieldFeature: boolean,
  linkedAccountsForCardShieldReady: boolean,
  linkedAccountsForCardShield: number
): {
  afterProductGateCardShieldCount: number;
  afterLinkedWaitCardShieldCount: number;
  afterLinkedBoundsCardShieldCount: number;
  afterEnrolledPairCardShieldCount: number;
} {
  const countCardShield = (rows: AdobeBannerDecision[]) => rows.filter((row) => isCardShieldBannerDecision(row)).length;

  let rows = adobeBannerData;
  if (!showCardShieldFeature) {
    rows = rows.filter((banner) => !isCardShieldBannerDecision(banner));
  }
  const afterProductGateCardShieldCount = countCardShield(rows);

  if (!linkedAccountsForCardShieldReady) {
    rows = rows.filter((banner) => !isCardShieldBannerDecision(banner));
  }
  const afterLinkedWaitCardShieldCount = countCardShield(rows);

  const afterLinkedAccountBounds = rows.filter((banner) => {
    if (!isCardShieldBannerDecision(banner)) {
      return true;
    }
    return cardShieldAdobeBannerMatchesLinkedAccountCount(banner, linkedAccountsForCardShield);
  });
  const afterLinkedBoundsCardShieldCount = countCardShield(afterLinkedAccountBounds);

  const afterEnrolledPair = filterCardShieldDashboardAccountKeyedBanners(
    afterLinkedAccountBounds,
    linkedAccountsForCardShield,
    linkedAccountsForCardShieldReady
  );

  return {
    afterProductGateCardShieldCount,
    afterLinkedWaitCardShieldCount,
    afterLinkedBoundsCardShieldCount,
    afterEnrolledPairCardShieldCount: countCardShield(afterEnrolledPair),
  };
}

/**
 * One-line explanation when product gate passed but the blue ribbon is not on screen.
 */
export function buildWhyRibbonHidden(
  input: CardShieldDashboardRibbonDecisionInput,
  decisionReasonCode: CardShieldDashboardRibbonDecisionCode
): string | null {
  const pathname = input.currentPathname ?? '';

  if (input.whiteLabelClient) {
    return `Adobe dashboard offers are disabled for white-label clients (whiteLabelClient="${input.whiteLabelClient}"). Member API cardShield can still be AVAILABLE; Target rows in window.adobeBannerTest are ignored by useAdobeConceptTesting.`;
  }

  if (!input.showCardShieldFeature || decisionReasonCode === 'product_feature_gate_blocked') {
    const status = input.memberApiCardShield?.featureStatus ?? '(missing)';
    const fulfillment = input.memberApiCardShield?.fulfillmentStatus ?? '(missing)';
    const hookCardShield = input.adobeBannerData.filter((row) => isCardShieldBannerDecision(row)).length;
    const targetCardShield = input.windowAdobeCardShieldRowCount ?? 0;
    const targetNote =
      targetCardShield > 0
        ? ` Target already has ${targetCardShield} Card Shield row(s) and the hook sees ${hookCardShield}; DSP still removes them until Member API sets cardShield to AVAILABLE.`
        : '';
    return `Member API productFeatures.cardShield is ${status} / ${fulfillment} — utils.cardShieldVisible is false.${targetNote} Fix Member API getCardShieldFeature (plan, partner, credit, TXM/FW, ENABLE_CARD_SHIELD), not Adobe.`;
  }

  if (decisionReasonCode === 'waiting_for_linked_accounts') {
    return 'Unified FM: finance connections still loading — Card Shield Adobe rows are stripped until linked account count is ready (Target may also still be loading).';
  }

  const windowTotal = input.windowAdobeBannerTestCount ?? 0;
  const hookTotal = input.adobeBannerData.length;
  if (windowTotal === 0) {
    return 'Adobe Target has not set window.adobeBannerTest (or it is empty). Confirm Target activity / QA mode on this page.';
  }
  if ((input.windowAdobeCardShieldRowCount ?? 0) > 0 && hookTotal === 0) {
    return `Target injected ${input.windowAdobeCardShieldRowCount} Card Shield row(s) on window.adobeBannerTest but useAdobeConceptTesting returned 0 rows — check cohort cookie (hide), conditions vs Redux auth/locks/transactions, or bannerCopy/lang.`;
  }

  if (decisionReasonCode === 'no_cardshield_rows_from_adobe') {
    return 'No Card Shield-shaped Adobe rows after the hook (ctaModal CARD_SHIELD_DASHBOARD_BANNER_INTRO_MODAL, /card-shield path, etc.).';
  }

  if (decisionReasonCode === 'cardshield_rows_filtered_out') {
    const breakdown = buildCardShieldRibbonFilterBreakdown(
      input.adobeBannerData,
      input.showCardShieldFeature,
      input.linkedAccountsForCardShieldReady,
      input.linkedAccountsForCardShield
    );
    return `Card Shield Adobe rows were removed by DSP filters (linkedAccounts=${
      input.linkedAccountsForCardShield
    }, breakdown=${JSON.stringify(breakdown)}).`;
  }

  if (decisionReasonCode === 'won_by_other_campaign' && input.chosenAdobeBanner) {
    return `Another Adobe campaign won priority (bannerId="${input.chosenAdobeBanner.bannerId}", bannerPriority=${input.chosenAdobeBanner.bannerPriority}). Lower bannerPriority number wins.`;
  }

  if (decisionReasonCode === 'no_winning_banner_after_priority') {
    return 'No Adobe row left after priority selection.';
  }

  if (decisionReasonCode === 'showing_cardshield_banner' && input.chosenAdobeBanner) {
    if (!cardShieldAdobeBannerRendersOnPath(input.chosenAdobeBanner, pathname)) {
      const showOn = input.chosenAdobeBanner.bannerToShowOn;
      return `Card Shield row won priority but AdobeBanner hides it on pathname "${pathname}" (bannerToShowOn=${JSON.stringify(
        showOn ?? 'dashboard-only default'
      )}).`;
    }
    return null;
  }

  if (decisionReasonCode === 'auth_or_plan_not_ready') {
    return 'Plan/productFeatures not hydrated yet.';
  }

  return null;
}

function ribbonEligibilityReadiness(input: CardShieldDashboardRibbonDecisionInput): string {
  if (!input.authReady) {
    return 'auth_or_plan_pending';
  }
  if (!input.showCardShieldFeature) {
    return 'product_feature_gate_blocked';
  }
  if (!input.linkedAccountsForCardShieldReady) {
    return 'waiting_for_linked_accounts';
  }
  return 'ready_for_adobe_priority';
}

/**
 * Explains why the dashboard blue Card Shield ribbon may or may not render.
 * @see docs/features/card-shield/card-shield-dashboard-ribbon-decision-contract.md
 */
export function logCardShieldDashboardRibbonDecision(input: CardShieldDashboardRibbonDecisionInput): void {
  if (!isCardShieldVisibilityDebugEnabled()) return;

  const decisionReasonCode = deriveCardShieldDashboardRibbonDecisionCode(input);
  const signature = buildRibbonLogSignature(input, decisionReasonCode);
  if (lastRibbonLogSignature === signature) {
    return;
  }
  lastRibbonLogSignature = signature;

  const rawCardShieldRows = input.adobeBannerData.filter((row) => isCardShieldBannerDecision(row));
  const afterFilterCardShieldRows = input.adobeBannerDataForPriority.filter((row) => isCardShieldBannerDecision(row));
  const whyRibbonHidden = buildWhyRibbonHidden(input, decisionReasonCode);
  const ribbonWillShow =
    decisionReasonCode === 'showing_cardshield_banner' &&
    !!input.chosenAdobeBanner &&
    cardShieldAdobeBannerRendersOnPath(input.chosenAdobeBanner, input.currentPathname ?? '');
  const filterBreakdown = buildCardShieldRibbonFilterBreakdown(
    input.adobeBannerData,
    input.showCardShieldFeature,
    input.linkedAccountsForCardShieldReady,
    input.linkedAccountsForCardShield
  );

  const decisionReasonContext: Record<string, string> = {
    auth_or_plan_not_ready: 'Auth or primary member plan not hydrated yet.',
    product_feature_gate_blocked:
      'utils.cardShieldVisible is false — Member API cardShield NOT_AVAILABLE (includes US-only and plan rules) or REACT_APP_CARD_SHIELD_ENABLED=false.',
    waiting_for_linked_accounts:
      'Unified FM: useConnectionsDetails still loading; Card Shield Adobe rows are stripped until count is ready.',
    no_cardshield_rows_from_adobe:
      'window.adobeBannerTest has no Card Shield rows (Target not injected, or useAdobeConceptTesting dropped them).',
    cardshield_rows_filtered_out:
      'Card Shield rows existed but were removed (product gate, linked-account wait, min/max bounds, or enrolled/not-enrolled pair).',
    no_winning_banner_after_priority: 'No banner left after priority sort.',
    won_by_other_campaign:
      'A non–Card Shield Adobe row won getHighestPriorityBannerDecision (lower bannerPriority number wins).',
    showing_cardshield_banner: 'Card Shield row won; AdobeBannerList should render the blue ribbon.',
  };

  const payload = {
    /** false when product gate passed but ribbon is still not on screen — read whyRibbonHidden */
    ribbonWillShow,
    whyRibbonHidden,
    decisionReasonCode,
    decisionReasonContext: decisionReasonContext[decisionReasonCode],
    ribbonEligibilityReadiness: ribbonEligibilityReadiness(input),
    cardShieldProductGate: input.showCardShieldFeature,
    memberApiProductFeaturesCardShield: input.memberApiCardShield ?? null,
    currentPathname: input.currentPathname,
    whiteLabelClient: input.whiteLabelClient || '(none — Adobe hook allowed)',
    linkedAccountsForRibbonTargeting: input.linkedAccountsForCardShield,
    linkedAccountsReady: input.linkedAccountsForCardShieldReady,
    isUnifiedTransactions: input.isUnifiedTransactions,
    adobeTarget: {
      windowAdobeBannerTestCount: input.windowAdobeBannerTestCount ?? 0,
      windowAdobeCardShieldRowCount: input.windowAdobeCardShieldRowCount ?? 0,
      windowAdobeCardShieldBannerIds: input.windowAdobeCardShieldBannerIds ?? [],
      adobeHookRowCount: input.adobeBannerData.length,
      adobeHookCardShieldRowCount: rawCardShieldRows.length,
      adobeCardShieldRowIdsInHookPayload: rawCardShieldRows.map((r) => r.bannerId),
      rowCountAfterCardShieldFilters: input.adobeBannerDataForPriority.length,
      adobeCardShieldRowCountAfterFilters: afterFilterCardShieldRows.length,
      adobeCardShieldRowIdsAfterFilters: afterFilterCardShieldRows.map((r) => r.bannerId),
      cardShieldFilterBreakdown: filterBreakdown,
    },
    chosenAdobeBanner: input.chosenAdobeBanner
      ? {
          bannerId: input.chosenAdobeBanner.bannerId,
          bannerPriority: input.chosenAdobeBanner.bannerPriority,
          cohort: (input.chosenAdobeBanner as {cohort?: string}).cohort,
          ctaModal: input.chosenAdobeBanner.ctaModal,
          bannerToShowOn: input.chosenAdobeBanner.bannerToShowOn,
          rendersOnCurrentPath: cardShieldAdobeBannerRendersOnPath(
            input.chosenAdobeBanner,
            input.currentPathname ?? ''
          ),
          isCardShieldBannerDecision: isCardShieldBannerDecision(input.chosenAdobeBanner),
        }
      : null,
    productFeaturesKeys: input.productFeaturesKeys,
    filterPipeline:
      'Target window.adobeBannerTest → useAdobeConceptTesting (disabled if whiteLabelClient) → cardShieldVisible → linked-account wait → min/max bounds → enrolled/not-enrolled pair → lowest bannerPriority wins → AdobeBanner bannerToShowOn',
  };

  if (input.showCardShieldFeature && !ribbonWillShow) {
    // eslint-disable-next-line no-console -- prominent when Member API gate passed but ribbon missing
    console.warn(CARD_SHIELD_DASHBOARD_RIBBON_LOG_TAG, payload);
  } else {
    // eslint-disable-next-line no-console -- opt-in / dev dashboard ribbon diagnostics
    console.log(CARD_SHIELD_DASHBOARD_RIBBON_LOG_TAG, payload);
  }
}
